import { useNavigate } from 'react-router-dom';
import { ArrowRight, Palette } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const engineeringBgRef = useRef(null);
  const originalsBgRef = useRef(null);
  const [bgImages, setBgImages] = useState({
    landing_bg_engineering: 'https://customer-assets.emergentagent.com/job_cad-craft-studio-1/artifacts/km5z58n0_Gemini_Generated_Image_g04itng04itng04i.png',
    landing_bg_originals: 'https://customer-assets.emergentagent.com/job_cad-craft-studio-1/artifacts/un3m7cda_ChatGPT%20Image%20Feb%202%2C%202026%2C%2012_52_42%20PM.png'
  });

  useEffect(() => {
    axios.get(`${API}/site-settings`).then(res => {
      setBgImages(prev => ({ ...prev, ...res.data }));
    }).catch(() => {});

    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleEngineeringMouseMove = useCallback((e) => {
    if (!engineeringBgRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    
    let pos;
    if (isDesktop) {
      const x = (e.clientX - rect.left) / rect.width;
      pos = `${20 + (x * 60)}% center`;
    } else {
      const y = (e.clientY - rect.top) / rect.height;
      pos = `center ${20 + (y * 60)}%`;
    }
    engineeringBgRef.current.style.backgroundPosition = pos;
  }, [isDesktop]);

  const handleEngineeringMouseLeave = useCallback(() => {
    if (!engineeringBgRef.current) return;
    engineeringBgRef.current.style.backgroundPosition = isDesktop ? '50% center' : 'center 50%';
  }, [isDesktop]);

  const handleOriginalsMouseMove = useCallback((e) => {
    if (!originalsBgRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    
    let pos;
    if (isDesktop) {
      const x = (e.clientX - rect.left) / rect.width;
      pos = `${20 + (x * 60)}% center`;
    } else {
      const y = (e.clientY - rect.top) / rect.height;
      pos = `center ${20 + (y * 60)}%`;
    }
    originalsBgRef.current.style.backgroundPosition = pos;
  }, [isDesktop]);

  const handleOriginalsMouseLeave = useCallback(() => {
    if (!originalsBgRef.current) return;
    originalsBgRef.current.style.backgroundPosition = isDesktop ? '50% center' : 'center 50%';
  }, [isDesktop]);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="landing-page">
      {/* Engineering Side - Black & Orange */}
      <div 
        className="split-panel flex-1 relative bg-black overflow-hidden cursor-pointer group"
        onClick={() => navigate('/engineering')}
        onMouseMove={handleEngineeringMouseMove}
        onMouseLeave={handleEngineeringMouseLeave}
        data-testid="engineering-panel"
      >
        {/* Background Image */}
        <div 
          ref={engineeringBgRef}
          className="absolute inset-0 bg-cover"
          style={{ 
            backgroundImage: `url(${bgImages.landing_bg_engineering})`,
            backgroundPosition: isDesktop ? '50% center' : 'center 50%',
            filter: 'brightness(0.65)',
            transform: 'scale(1.15)',
            transition: 'background-position 0.15s ease-out'
          }}
        />
        
        {/* Orange accent overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#F54021]/15 to-transparent" />
        
        {/* Content - smaller on mobile */}
        <div className="relative z-10 h-full min-h-[45vh] lg:min-h-screen flex flex-col justify-center items-center p-4 lg:p-16">
          <div className="opacity-0 animate-slide-up animation-delay-100" style={{ animationFillMode: 'forwards' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_cad-craft-studio-1/artifacts/jhyl88a1_Picture1.png" 
              alt="BJe Logo" 
              className="w-32 h-32 lg:w-80 lg:h-80 object-contain mb-2 lg:mb-6"
              style={{ filter: 'brightness(0) saturate(100%) invert(31%) sepia(93%) saturate(2032%) hue-rotate(351deg) brightness(101%) contrast(95%)' }}
            />
          </div>
          
          <h1 className="font-nunito text-3xl lg:text-8xl font-bold text-[#F54021] text-center mb-2 lg:mb-4 opacity-0 animate-slide-up animation-delay-200" style={{ animationFillMode: 'forwards' }}>
            BJ Engineering
          </h1>
          
          <p className="font-nunito text-base lg:text-2xl text-white text-center max-w-md mb-4 lg:mb-8 opacity-0 animate-slide-up animation-delay-300 font-semibold" style={{ animationFillMode: 'forwards' }}>
            Your problems, solved
          </p>
          
          <div className="opacity-0 animate-slide-up animation-delay-400" style={{ animationFillMode: 'forwards' }}>
            <button 
              className="px-6 py-3 lg:px-8 lg:py-4 bg-[#F54021] text-white font-nunito font-bold rounded-full flex items-center gap-2 lg:gap-3 text-sm lg:text-base hover:bg-[#ff5a35] transition-colors shadow-lg shadow-[#F54021]/30"
              data-testid="explore-services-btn"
            >
              Explore Services
              <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Originals Side - Dark with Stag Head */}
      <div 
        className="split-panel flex-1 relative bg-[#0a0a0a] overflow-hidden cursor-pointer group"
        onClick={() => navigate('/originals')}
        onMouseMove={handleOriginalsMouseMove}
        onMouseLeave={handleOriginalsMouseLeave}
        data-testid="originals-panel"
      >
        {/* Background Image */}
        <div 
          ref={originalsBgRef}
          className="absolute inset-0 bg-cover"
          style={{ 
            backgroundImage: `url(${bgImages.landing_bg_originals})`,
            backgroundPosition: isDesktop ? '50% center' : 'center 50%',
            filter: 'brightness(0.6)',
            transform: 'scale(1.15)',
            transition: 'background-position 0.15s ease-out'
          }}
        />
        
        {/* Vignette Effect */}
        <div className="absolute inset-0 vignette" />
        
        {/* Grain Overlay */}
        <div className="absolute inset-0 grain-overlay" />
        
        {/* Content - smaller on mobile */}
        <div className="relative z-10 h-full min-h-[45vh] lg:min-h-screen flex flex-col justify-center items-center p-4 lg:p-16">
          <div className="opacity-0 animate-slide-up animation-delay-100" style={{ animationFillMode: 'forwards' }}>
            <div className="w-12 h-12 lg:w-20 lg:h-20 rounded-full border-2 border-[#d4af37] flex items-center justify-center mb-4 lg:mb-8">
              <Palette className="w-6 h-6 lg:w-10 lg:h-10 text-[#d4af37]" />
            </div>
          </div>
          
          <h1 className="font-playfair text-2xl lg:text-6xl font-semibold text-white text-center mb-2 lg:mb-4 opacity-0 animate-slide-up animation-delay-200" style={{ animationFillMode: 'forwards' }}>
            Ben Jefferies Originals
          </h1>
          
          <p className="font-cormorant text-base lg:text-2xl text-[#d4af37] text-center max-w-md mb-4 lg:mb-8 opacity-0 animate-slide-up animation-delay-300 italic" style={{ animationFillMode: 'forwards' }}>
            Where Engineering Meets Artistry
          </p>
          
          <div className="opacity-0 animate-slide-up animation-delay-400" style={{ animationFillMode: 'forwards' }}>
            <button 
              className="btn-originals px-6 py-3 lg:px-8 lg:py-4 rounded-full font-cinzel font-medium flex items-center gap-2 lg:gap-3 text-sm lg:text-base"
              data-testid="view-collection-btn"
            >
              View Collection
              <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
